import { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import proposalSampleServices from '../../services/proposalSampleServices';
import proposalsServices from '../../services/proposalsServices';
import { AuthContext } from '../../context/AuthProvider';

const detectSampleType = (sample) => {
  if (sample.sampleType) return sample.sampleType;
  const hasInstallation = (sample.scopeOfWorks?.length > 0) || (Number(sample.installationPrice) > 0) || (sample.systemTemplates?.length > 0) || !!sample.jobOverview;
  const hasService = (sample.workDescriptions?.length > 0) || !!sample.equipmentOverview || (Number(sample.jobPrice) > 0);
  if (hasInstallation && !hasService) return 'Installation';
  if (hasService && !hasInstallation) return 'Service';
  return null;
};

/**
 * Dialog for loading a saved sample into current proposal
 */
const LoadSampleDialog = ({ open, onClose, proposalId, proposalType, onApply }) => {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { userData } = useContext(AuthContext);
  const confirm = useConfirm();

  useEffect(() => {
    if (open) {
      loadSamples();
    }
  }, [open]);

  const loadSamples = async () => {
    try {
      setLoading(true);
      const response = await proposalSampleServices.getAll();
      if (response.status === 200) {
        setSamples(response.data || []);
      }
    } catch (error) {
      console.error('Error loading samples:', error);
      toast.error('Failed to load samples');
    } finally {
      setLoading(false);
    }
  };

  const adjustExpireDates = (items) => {
    if (!items || items.length === 0) return items;
    const endOfMonth = moment().endOf('month').format('YYYY-MM-DD');
    return items.map(item => ({
      ...item,
      expires: item.expires ? endOfMonth : item.expires,
    }));
  };

  const buildUpdateData = (sample) => {
    const commonFields = {
      warranties: sample.warranties || [],
      discounts: adjustExpireDates(sample.discounts || []),
      guarantees: sample.guarantees || [],
      paymentNotes: sample.paymentNotes || '',
    };

    if (!proposalType) {
      return {
        ...commonFields,
        jobOverview: sample.jobOverview || '',
        scopeOfWorks: sample.scopeOfWorks || [],
        contractRebates: adjustExpireDates(sample.contractRebates || []),
        installationPrice: Number(sample.installationPrice) || 0,
        equipmentOverview: sample.equipmentOverview || '',
        workDescriptions: sample.workDescriptions || [],
        jobPrice: Number(sample.jobPrice) || 0,
      };
    }

    if (proposalType === 'Service') {
      return {
        ...commonFields,
        equipmentOverview: sample.equipmentOverview || '',
        workDescriptions: sample.workDescriptions || [],
        jobPrice: Number(sample.jobPrice) || 0,
      };
    }
    return {
      ...commonFields,
      jobOverview: sample.jobOverview || '',
      scopeOfWorks: sample.scopeOfWorks || [],
      contractRebates: adjustExpireDates(sample.contractRebates || []),
      installationPrice: Number(sample.installationPrice) || 0,
    };
  };

  const handleApply = async (sample) => {
    const sType = detectSampleType(sample);
    const isCrossType = sType && proposalType && sType !== proposalType;

    const description = isCrossType
      ? `This sample was saved from a ${sType} proposal, but the current proposal is ${proposalType}. Only matching fields (warranties, discounts, guarantees) will be applied. Continue?`
      : `Apply "${sample.name}" to this proposal? This will replace existing values.`;

    confirm({
      title: isCrossType ? 'Different Proposal Type' : 'Apply Sample',
      description,
      confirmationText: 'Apply',
      cancellationText: 'Cancel',
    })
      .then(async () => {
        setApplying(sample.id);
        try {
          const updateData = buildUpdateData(sample);
          const response = await proposalsServices.update(proposalId, updateData);
          
          if (response.status === 200) {
            if (isCrossType) {
              toast.info(`Common fields from "${sample.name}" applied. Type-specific fields were skipped.`);
            } else {
              toast.success(`Sample "${sample.name}" applied successfully!`);
            }
            onApply && onApply();
            handleClose();
          }
        } catch (error) {
          console.error('Error applying sample:', error);
          toast.error('Failed to apply sample');
        } finally {
          setApplying(null);
        }
      })
      .catch(() => {});
  };

  const handleDelete = async (sample) => {
    confirm({
      title: 'Delete Sample',
      description: `Are you sure you want to delete "${sample.name}"? This cannot be undone.`,
      confirmationText: 'Delete',
      confirmationButtonProps: { color: 'error' },
    })
      .then(async () => {
        try {
          const response = await proposalSampleServices.remove(sample.id);
          if (response.status === 200) {
            toast.success('Sample deleted');
            loadSamples();
          }
        } catch (error) {
          console.error('Error deleting sample:', error);
          toast.error('Failed to delete sample');
        }
      })
      .catch(() => {});
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  // Filter samples by search query
  const filteredSamples = samples.filter(sample => 
    sample.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (sample.description && sample.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Separate own samples and shared samples
  const ownSamples = filteredSamples.filter(s => s.createdById === userData?.id);
  const sharedSamples = filteredSamples.filter(s => s.createdById !== userData?.id);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Load Sample</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : samples.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">
              No samples saved yet. Use "Save Sample" to create one from an existing proposal.
            </Typography>
          </Box>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder="Search samples..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Own Samples */}
            {ownSamples.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ px: 1, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon fontSize="small" />
                  My Samples
                </Typography>
                <List disablePadding>
                  {ownSamples.map((sample) => (
                    <SampleListItem
                      key={sample.id}
                      sample={sample}
                      isOwn={true}
                      applying={applying}
                      onApply={handleApply}
                      onDelete={handleDelete}
                      proposalType={proposalType}
                    />
                  ))}
                </List>
              </>
            )}

            {ownSamples.length > 0 && sharedSamples.length > 0 && <Divider sx={{ my: 2 }} />}

            {/* Shared Samples */}
            {sharedSamples.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ px: 1, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ShareIcon fontSize="small" />
                  Shared Samples
                </Typography>
                <List disablePadding>
                  {sharedSamples.map((sample) => (
                    <SampleListItem
                      key={sample.id}
                      sample={sample}
                      isOwn={false}
                      applying={applying}
                      onApply={handleApply}
                      onDelete={handleDelete}
                      proposalType={proposalType}
                    />
                  ))}
                </List>
              </>
            )}

            {filteredSamples.length === 0 && searchQuery && (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                No samples match your search
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const SampleListItem = ({ sample, isOwn, applying, onApply, onDelete, proposalType }) => {
  const isApplying = applying === sample.id;
  const sType = detectSampleType(sample);
  const isCrossType = sType && proposalType && sType !== proposalType;

  return (
    <ListItem
      disablePadding
      sx={{
        borderRadius: 1,
        mb: 0.5,
        '&:hover': { backgroundColor: '#f5f5f5' },
        ...(isCrossType && { opacity: 0.7, backgroundColor: '#fff8e1' }),
      }}
    >
      <ListItemButton onClick={() => onApply(sample)} disabled={isApplying}>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {sample.name}
              </Typography>
              {sType && (
                <Chip
                  label={sType === 'Service' ? 'SVC' : 'HIC'}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, backgroundColor: sType === 'Service' ? '#ed6c02' : '#1976d2', color: '#fff' }}
                />
              )}
              {sample.isShared && isOwn && (
                <Chip label="Shared" size="small" color="info" variant="outlined" sx={{ height: 18 }} />
              )}
              {isCrossType && (
                <Tooltip title={`This is a ${sType} sample — only common fields will apply`}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: '#ed6c02' }} />
                </Tooltip>
              )}
            </Box>
          }
          secondary={
            <Box>
              {sample.description && (
                <Typography variant="caption" color="textSecondary" display="block">
                  {sample.description}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">
                {!isOwn && `by ${sample.createdBy?.fullName || 'Unknown'} • `}
                {moment(sample.createdAt).format('MMM D, YYYY')}
              </Typography>
            </Box>
          }
        />
        <ListItemSecondaryAction>
          {isApplying ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <Tooltip title="Apply to proposal">
                <IconButton 
                  edge="end" 
                  onClick={(e) => { e.stopPropagation(); onApply(sample); }}
                  color="primary"
                  size="small"
                >
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
              {isOwn && (
                <Tooltip title="Delete">
                  <IconButton 
                    edge="end" 
                    onClick={(e) => { e.stopPropagation(); onDelete(sample); }}
                    color="error"
                    size="small"
                    sx={{ ml: 0.5 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </ListItemSecondaryAction>
      </ListItemButton>
    </ListItem>
  );
};

export default LoadSampleDialog;

